// src/context/AuthContext.tsx
import React, { createContext, useState, useEffect, useContext } from 'react';
import axiosClient from '../api/axiosClient';
import { User } from '../types';

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    login: (email: string, password: string) => Promise<void>;
    logout: () => void;
    /** Обновить профиль в состоянии и в кэше — после PATCH /v1/users/me. */
    updateUser: (next: User) => void;
    loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setAuthenticated] = useState(false);

    const login = async (email: string, password: string) => {
        setLoading(true);
        try {
            // 1. Пытаемся войти
            const response = await axiosClient.post('/login', { email, password });

            if (response.data.token) {
                const token = response.data.token;
                localStorage.setItem('jwt_token', token);
                axiosClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;

                // 2. СНАЧАЛА получаем юзера, потом ставим статус
                const userResponse = await axiosClient.get('/users/me');
                setUser(userResponse.data);

                // 🚀 ТОЛЬКО ТЕПЕРЬ авторизован!
                setAuthenticated(true);
            } else {
                throw new Error('Токен не получен');
            }
        } catch (error: any) {
            // 🚀 Чистим всё при ошибке
            localStorage.removeItem('jwt_token');
            delete axiosClient.defaults.headers.common['Authorization'];
            setAuthenticated(false);
            setUser(null);

            console.error('Login failed:', error);
            // Выбрасываем ошибку, чтобы LoginPage её поймал
            throw error;
        } finally {
            setLoading(false);
        }
    };


    const updateUser = (next: User) => {
        setUser(next);
        try {
            localStorage.setItem('user', JSON.stringify(next));
        } catch {
            /* localStorage может быть недоступен (приватный режим) */
        }
    };

    const logout = () => {
        localStorage.removeItem('jwt_token'); // <--- Ключ как в axiosClient!
        localStorage.removeItem('user');
        delete axiosClient.defaults.headers.common['Authorization'];
        setUser(null);
        // Кэш API на устройстве общий: Workbox ключует ответы по URL, без токена.
        // Чистим его при выходе, иначе офлайн-режим покажет данные прошлого аккаунта.
        if (typeof caches !== 'undefined') {
            caches.delete('api-cache').catch(() => {
                /* кэш не критичен для выхода — молча продолжаем */
            });
        }
        // Опционально: редирект, чтобы сбросить все стейты
        window.location.href = '/';
    };

    useEffect(() => {
        const checkAuth = async () => {
            const token = localStorage.getItem('jwt_token');
            const savedUser = localStorage.getItem('user');

            // 1. ОПТИМИСТИЧНЫЙ ВХОД: Если есть и токен, и данные юзера — пускаем сразу!
            if (token && savedUser) {
                try {
                    const parsedUser = JSON.parse(savedUser);
                    setUser(parsedUser);
                    setAuthenticated(true);
                    setLoading(false); // 🚀 ЧАТЫ ОТКРОЮТСЯ МГНОВЕННО ТУТ

                    // Прописываем заголовок для будущих запросов
                    axiosClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                } catch (e) {
                    console.error("Saved user corrupted");
                }
            }

            // 2. ФОНОВАЯ ПРОВЕРКА: Актуализируем данные с сервера Jino
            if (token) {
                try {
                    axiosClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                    const response = await axiosClient.get('/users/me');

                    // Обновляем стейт и кэш свежими данными (например, если баланс изменился)
                    setUser(response.data);
                    localStorage.setItem('user', JSON.stringify(response.data));
                    setAuthenticated(true);
                } catch (error: any) {
                    // Токен протух (401/403) — выкидываем на логин. Сетевой сбой
                    // или 5xx значит лишь «сервер недоступен»: остаёмся в приложении
                    // на сохранённом профиле, иначе офлайн-режим не покажет кэш.
                    const status = error?.response?.status;
                    const isAuthError = status === 401 || status === 403;
                    if (isAuthError || !savedUser) {
                        console.error("Auth check failed:", error);
                        logout();
                    } else {
                        console.warn("Auth check failed (сеть недоступна), работаем на кэше:", error);
                    }
                } finally {
                    setLoading(false);
                }
            } else {
                // Токена нет совсем
                setAuthenticated(false);
                setLoading(false);
            }
        };
        checkAuth();
    }, []);



    return (
        <AuthContext.Provider value={{ user, isAuthenticated, login, logout, updateUser, loading }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};